/**
 * The slate-api wire contract, as the browser sees it.
 *
 * These mirror the Prisma enums and route return shapes in the sibling repo
 * (slate-api/prisma/schema.prisma, slate-api/src/routes/*.ts). Dates arrive as
 * ISO strings (JSON has no Date), so they are typed as `string` here and
 * parsed at the edge where they are rendered.
 */

export type Role = 'user' | 'admin' | 'owner';
export type MembershipStatus = 'active' | 'suspended';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled';
export type VideoStatus = 'uploading' | 'processing' | 'ready' | 'failed';
/**
 * What was captured. The API derives this from the upload's content type (a
 * client never sends it), and it decides whether there is a picture at all: an
 * `audio` row has no poster and no HLS ladder, only a progressive AAC/MP4.
 */
export type VideoKind = 'screen' | 'audio';
export type Workspace = 'personal' | 'team';
export type Visibility = 'public' | 'private';
export type TranscriptStatus = 'pending' | 'ready' | 'failed';

/** Error envelope: every 4xx/5xx from slate-api looks like this. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// ---------------------------------------------------------------------------
// Videos
// ---------------------------------------------------------------------------

/** A row in the library grid: GET /v1/videos. */
export interface VideoSummary {
  id: string;
  shareId: string;
  title: string;
  status: VideoStatus;
  kind: VideoKind;
  durationSec: number | null;
  posterKey: string | null;
  visibility: Visibility;
  workspace: Workspace;
  orgId: string | null;
  ownerId: string | null;
  createdAt: string;
  readyAt: string | null;
  /**
   * `reactions` is optional on purpose. GET /v1/videos does not count them
   * today, so the library card renders the number only when the API starts
   * sending one. Nothing is guessed or derived in its absence.
   */
  _count: { views: number; comments: number; reactions?: number };
}

export interface VideoListResponse {
  videos: VideoSummary[];
  nextCursor: string | null;
}

/** GET /v1/videos/:id. The full row, plus relations. */
export interface VideoDetail extends Omit<VideoSummary, '_count'> {
  requestLinkId: string | null;
  width: number | null;
  height: number | null;
  submitterName: string | null;
  hlsPrefix: string | null;
  mp4Key: string | null;
  updatedAt: string;
  deletedAt: string | null;
  transcript: { status: TranscriptStatus; vttKey: string | null } | null;
  _count: { views: number; comments: number };
}

export interface UploadPart {
  partNumber: number;
  url: string;
}

/** POST /v1/videos */
export interface CreateVideoResponse {
  videoId: string;
  shareId: string;
  uploadId: string;
  parts: UploadPart[];
}

export interface CreateVideoBody {
  contentType: string;
  workspace?: Workspace;
  orgId?: string;
  title?: string;
  partCount?: number;
}

/** POST /v1/videos/:id/parts */
export interface MorePartsResponse {
  parts: UploadPart[];
}

export interface CompletedPart {
  partNumber: number;
  etag: string;
}

/** POST /v1/videos/:id/complete */
export interface CompleteVideoResponse {
  id: string;
  shareId: string;
  status: VideoStatus;
  title: string;
}

// ---------------------------------------------------------------------------
// Share / playback
// ---------------------------------------------------------------------------

export interface Playback {
  /** Null for an audio note: there is no ladder to build from one track. */
  hlsUrl: string | null;
  mp4Url: string | null;
  /** Null for an audio note: there is no frame to grab. */
  posterUrl: string | null;
  captionsUrl: string | null;
}

/** GET /v1/share/:shareId. `playback` is null until status === 'ready'. */
export interface SharePayload {
  shareId: string;
  title: string;
  status: VideoStatus;
  kind: VideoKind;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  createdAt: string;
  readyAt: string | null;
  author: { name: string | null; avatarUrl: string | null } | null;
  playback: Playback | null;
  /**
   * The original upload, for the owner and their team, while `playback` is
   * still null. Short lived; the page swaps to `playback` when it arrives.
   */
  rawPlaybackUrl: string | null;
  /** The recorder's first frame, then the transcoder's poster. Null for audio. */
  posterUrl: string | null;
  /** Null for videos that predate transcription. */
  transcript: { status: 'pending' | 'ready' | 'failed'; language: string; captionsUrl: string | null } | null;
}

export interface Comment {
  id: string;
  authorName: string | null;
  bodyText: string;
  timecodeSec: number | null;
  createdAt: string;
  resolvedAt?: string | null;
  authorUser?: { id: string; name: string | null; avatarUrl: string | null } | null;
}

export interface CommentsResponse {
  comments: Comment[];
}

// ---------------------------------------------------------------------------
// Reactions
// ---------------------------------------------------------------------------

/**
 * A fixed vocabulary, mirroring the `ReactionKind` enum in the API. The wire
 * value is a key, never the emoji: the glyph is a rendering choice that can be
 * swapped without a migration, and a public endpoint that accepted arbitrary
 * strings would be a liability.
 */
export const REACTION_KINDS = ['like', 'love', 'laugh', 'wow', 'clap', 'think'] as const;

export type ReactionKind = (typeof REACTION_KINDS)[number];

/** One reaction, pinned to the second of the recording it belongs to. */
export interface Reaction {
  id: string;
  kind: ReactionKind;
  timecodeSec: number;
  createdAt: string;
}

export type ReactionTotals = Record<ReactionKind, number>;

/**
 * GET /v1/share/:shareId/reactions. Ordered by `timecodeSec`, capped at 1000
 * rows, with the totals counted server-side so a truncated list still reports
 * an honest number.
 */
export interface ReactionsResponse {
  reactions: Reaction[];
  totals: ReactionTotals;
}

/** POST /v1/share/:shareId/reactions. `anonId` stands in for a signed-in id. */
export interface CreateReactionBody {
  kind: ReactionKind;
  timecodeSec: number;
  anonId?: string;
}

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

/** GET /v1/orgs: one entry per active membership, with the caller's role. */
export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt: string | null;
  seatCount: number;
  role: Role;
}

export interface OrgMember {
  role: Role;
  createdAt: string;
  user: { id: string; email: string; name: string | null; avatarUrl: string | null };
}

export interface OrgInvite {
  id: string;
  email: string;
  role: Role;
  expiresAt: string;
  createdAt: string;
  /**
   * The link to hand the invitee. Null for members who cannot invite, since the
   * token in it is a bearer credential for a seat.
   */
  acceptUrl: string | null;
}

/** GET /v1/orgs/:id */
export interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt: string | null;
  seatCount: number;
  memberships: OrgMember[];
  invites: OrgInvite[];
}

/** POST /v1/orgs/:id/invites */
export interface CreateInviteResponse {
  invite: {
    id: string;
    email: string;
    role: Role;
    expiresAt: string;
    acceptUrl: string;
  };
}

/** POST /v1/orgs: checkoutUrl is null when Stripe is not configured. */
export interface CreateOrgResponse {
  org: {
    id: string;
    name: string;
    slug: string;
    subscriptionStatus: SubscriptionStatus;
    trialEndsAt: string | null;
  };
  checkoutUrl: string | null;
}

/** POST /v1/invites/:token/accept */
export interface AcceptInviteResponse {
  org: { id: string; name: string; slug: string };
  role: Role;
}

export interface BillingPortalResponse {
  url: string;
}

// ---------------------------------------------------------------------------
// Request links
// ---------------------------------------------------------------------------

export interface RequestLink {
  id: string;
  token: string;
  label: string;
  disabled: boolean;
  expiresAt: string | null;
  maxVideos: number | null;
  createdAt: string;
  _count: { videos: number };
}

export interface RequestLinksResponse {
  links: RequestLink[];
}

/** POST /v1/request-links returns the row without `disabled`/`_count`. */
export interface CreatedRequestLink {
  id: string;
  token: string;
  label: string;
  expiresAt: string | null;
  maxVideos: number | null;
  createdAt: string;
}

/** GET /v1/r/:token. Public, deliberately says nothing about the requester. */
export interface RequestLinkPublic {
  label: string;
  requestedBy: string;
  maxUploadBytes: number;
}
